/**
 * The project settings screen (`KLQ-20260819-004` tab 7).
 *
 * Two copy decisions carry weight here, both about consequences a person cannot see:
 *
 * `naming.locked` says the template is on published paper, not that it is "locked" — the word
 * on its own reads as a permission the reader lacks, when in fact they may change it and are
 * being told what it costs.
 *
 * `identifiers.stateBuildingId.help` says write-once **before** the field is filled, because
 * the refusal afterwards is a `409` that arrives too late to be advice.
 */

export const projectSettingsEn = {
  'page.title': 'Project settings',
  'page.subtitle': 'The project’s own details, and how its drawings are named.',

  'status.loading': 'Loading the project…',
  'status.readOnly': 'You can see these settings but not change them.',
  'status.saved': 'Saved.',

  'general.title': 'General',
  'general.name': 'Project name',
  'general.client': 'Client',
  'general.address': 'Address',
  'general.phase': 'Phase',
  'general.startsOn': 'Starts',
  'general.endsOn': 'Ends',
  'general.save': 'Save changes',
  'general.nothingToSave': 'Nothing has changed yet.',

  // The three statutory codes of KLQ-20260817-001. Grouped away from the general fields
  // because two of them are the state's values rather than the studio's, and one of them
  // cannot be taken back.
  'identifiers.title': 'Identification',
  'identifiers.intro':
    'The studio’s own short code, and the two codes the state issues. All three can appear in drawing file names.',
  'identifiers.shortCode': 'Short code',
  'identifiers.shortCode.help': 'Your own abbreviation for this project, like REZ, ADM, SKOLA.',
  'identifiers.stateBuildingId': 'ID stavby',
  'identifiers.stateBuildingId.help':
    'Issued by the state information system. It can be set once and never changed, because it travels inside file names that reach a state repository.',
  'identifiers.stateBuildingId.set': 'Set, and not changeable.',
  'identifiers.classificationCode': 'Classification code',
  'identifiers.classificationCode.help': 'Four digits from the ÚPaV list of building types.',

  'naming.title': 'Drawing names',
  'naming.intro':
    'Every drawing’s name is rendered from where it sits in the tree. Nobody types one.',
  'naming.template': 'Naming template',
  'naming.preview': 'A drawing would be called',
  'naming.locked':
    'Drawings have already been published under this template, so a change is recorded with a reason. Names already on paper are not rewritten.',
  'naming.reason': 'Why is it changing?',
  'naming.reasonPlaceholder': 'The client asked for the block letter to come first…',
  'naming.save': 'Change the template',
  'naming.error.grammar': 'That template cannot be used.',
  'naming.error.locked': 'That change needs a reason, because drawings have been published.',

  'disciplines.title': 'Disciplines',
  'disciplines.intro':
    'Who draws in this project. The statutory code goes into drawing file names; the practice code is only how the studio sorts its folders.',
  'disciplines.empty': 'This project has no disciplines yet.',

  // The vocabulary editor (MCK-20260812-011). `vocabulary.archive.body` says what archiving
  // does *not* do before the click, for the same reason `archive.body` does below.
  'vocabulary.code': 'Code',
  'vocabulary.statutoryCode': 'Statutory code',
  'vocabulary.practiceCode': 'Practice code',
  'vocabulary.qualifier': 'Qualifier',
  'vocabulary.label': 'Name',
  'vocabulary.edit': 'Edit',
  'vocabulary.save': 'Save',
  'vocabulary.cancel': 'Cancel',
  'vocabulary.reason': 'Why is it changing?',
  'vocabulary.reasonPlaceholder': 'The authority asked for the annex-24 code…',
  'vocabulary.error.conflict': 'That change was refused. Add a reason, or pick another code.',
  'vocabulary.archive.action': 'Archive',
  'vocabulary.archive.body':
    'Drawings that already use it keep it. It will not be offered for new ones, and this cannot be undone.',
  'vocabulary.archive.confirm': 'Yes, archive it',

  'types.title': 'Document types',
  'types.intro':
    'What a drawing can be in this project. The generator turns a selection of these into one empty drawing per level.',
  'types.empty': 'This project has no document types yet.',

  // The door into the project-shape screen (`KLQ-20260825-007`). "Structure" and not
  // "generation": what it does is decide how a project divides, and the mechanism that writes
  // it is not what somebody comes here looking for.
  //
  // The body used to promise that nothing there moves anything. That was true of phase 1 and
  // is not true of phase 2, so it now names the four operations instead. A door that
  // under-promises what is behind it is still a door that lies.
  'shape.title': 'Project structure',
  'shape.body':
    'Blocks, floors, units, and which drawings each of them carries. Add levels, move one somewhere else, copy one, or put a new level above the ones that grew together.',
  'shape.action': 'Open the project structure',

  'archive.title': 'Archive this project',
  'archive.body':
    'An archived project stays readable and stays exportable. Nothing is deleted, and nobody loses a drawing.',
  'archive.action': 'Archive the project',
  'archive.confirm': 'Yes, archive it',
  'archive.cancel': 'Keep it open',
  'archive.done': 'This project is archived.',

  'stale.title': 'Somebody else changed this project',
  'stale.body': 'Your changes were not saved. Here is what the project says now:',
  'stale.reload': 'Load the current version',

  'error.forbidden': 'You are not allowed to change this project.',
  'error.gone': 'This project is no longer here.',
  'error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'error.validation': 'Some of those values were refused. Check the fields and try again.',
  'error.writeOnce': 'That value is already set and cannot be changed.',
  'error.unexpected': 'Something went wrong. Try again.',
} as const;

export type ProjectSettingsMessageKey = keyof typeof projectSettingsEn;
